import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DialogueRegisterState,
  IActionRegister,
  INext,
  IRegisterAction,
  actionsComplete,
  next,
  registerActionResult,
} from 'core/slices/register';
import { actionRequest, attemptRequest, completeRequest, dialogueRequest } from 'core/services/product';
import { IActionAction } from 'types/action/player';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { selectToken } from 'core/selectors/product/token';
import { IToken, Mode, TokenState, updateTokenState } from 'core/slices/session';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { IPlayerReducer, ViewerState } from 'core/slices/player';
import { LoadingState } from 'types/domain/base';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { CourseNodeRole, ICourseNodeDAO } from 'types/domain/course';

function* handleRegister(action: PayloadAction<IRegisterAction>) {
  if (!action.payload.sessionAttemptId) return;

  const mode: Mode = yield select(selectSessionMode);

  if (mode !== Mode.LEARNING) return;

  const actionRegister: IActionRegister = yield select((state: RootState) =>
    state.register.attempts[action.payload.sessionAttemptId]
      ? state.register.attempts[action.payload.sessionAttemptId].actions[action.payload.actionId]
      : null,
  );

  if (!actionRegister || !action.payload.actionId) return console.warn('No action register');

  //@ts-ignore
  const actionDAO: IActionBasic = yield select(state => selectAction(state, action.payload.actionId));

  if (!action.payload.result) return;

  const token: IToken = yield select(selectToken);

  //if (token && token.state === TokenState.CAN_RESTART) yield take(updateTokenState);

  let sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  if (sessionAttemptId < 0) {
    const { payload } = yield take(attemptRequest.fulfilled);
    sessionAttemptId = payload.id;
  }

  const register: IActionAction = {
    ...action.payload,
    result: actionRegister.result,
    created: actionRegister.created,
    started: actionRegister.started,
    completed: actionRegister.completed,
    entities: Object.values(actionRegister.entities),
    sessionAttemptId,
  };

  yield put(yield call(actionRequest, register));
}

function* handleNext(action: PayloadAction<INext>) {
  const mode: Mode = yield select(selectSessionMode);
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  const player: IPlayerReducer = yield select((state: RootState) => state.core.player);

  if (
    player.loading.action !== LoadingState.LOADING &&
    player.state.viewer.state !== ViewerState.TRANSITIONING &&
    actionFeed.feed.length === actionFeed.index + 1 &&
    action.payload.interaction
  ) {
    if (mode === Mode.LEARNING) {
      const token: IToken = yield select(selectToken);

      const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);

      if (actionFeed.feed[actionFeed.index].actionId)
        yield put(
          yield call(completeRequest, {
            sessionAttemptId,
            courseProgressId: token.courseProgressId,
          }),
        );
      else if (actionFeed.feed[actionFeed.index].courseNodeId) {
        const groupIds = yield select((state: RootState) => Object.keys(state.session.groups));
        const courseNode: ICourseNodeDAO = yield select(state =>
          selectCourseNode(state, actionFeed.feed[actionFeed.index].courseNodeId),
        );
        yield put(
          yield call(dialogueRequest, {
            token: token.token,
            courseNodeId: actionFeed.feed[actionFeed.index].courseNodeId,
            courseProgressId: token.courseProgressId,
            state: DialogueRegisterState.SUBMITTED,
            groupIds: courseNode.entityType === CourseNodeRole.GROUP ? groupIds : [],
            autoNext: true,
          }),
        );
      }
    } else {
      yield put(yield call(actionsComplete));
    }
  }
}

function* watchRegister() {
  yield takeEvery(registerActionResult.type, handleRegister);
}

function* watchNext() {
  yield takeEvery(next, handleNext);
}

export function* requestSaga() {
  yield all([fork(watchRegister), fork(watchNext)]);
}
